import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBeforeUnload } from "react-router-dom";
import UserAccessSummary from "../components/layout/UserAccessSummary";
import useAccessOverrideVersion from "../hooks/useAccessOverrideVersion";
import { getCurrentUser } from "../utils/auth";
import { ALL_ACCESS_TOKEN, NULL_ACCESS_TOKEN, getUserAccessInfo } from "../utils/accessFilters";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";
const API_ERROR_MAP = {
  s3_not_configured: "File storage is not configured on the backend.",
  storage_upload_failed: "File upload failed in backend storage.",
  s3_upload_failed: "File upload failed in backend storage.",
  template_download_failed: "Template file could not be loaded from backend storage.",
  template_not_uploaded: "Please upload a template file first.",
  data_dump_not_uploaded: "Please upload a data dump file first.",
  cust_code_not_allowed: "You do not have access to this Cust Code.",
  read_only_role: "Your role is read-only for this action.",
  supplier_flow_items_mismatch:
    "Supplier Flow item rows do not match the current version. Re-download the latest Filled Ladder Plan and update only weekly values.",
  supplier_oh_read_only: "Supplier OH is system-derived and cannot be edited in this update.",
  event_forecast_item_not_in_data_dump:
    "Event Forecast contains an Item Nbr that does not exist in the current Data Dump.",
  template_reingest_failed: "Template weekly sheets could not be re-ingested after the Data Dump upload.",
  weekly_update_failed: "Update Supplier Flow / Event Forecast failed on the backend.",
  whse_code_required: "Please select a warehouse.",
  whse_code_not_allowed: "You do not have access to this warehouse.",
  allocation_scope_required: "Select at least one item/week scope before saving allocation.",
  invalid_allocation_mode: "Allocation mode is invalid.",
  measure_keys_required: "Select at least one measure to allocate.",
  allocation_base_rows_not_found: "No ladder MRP series rows were found for the selected scope.",
  allocation_exceeds_base_qty: "Allocation exceeds the available base quantity.",
  allocation_qty_invalid: "Allocation quantity must be zero or greater.",
  weekly_update_anchor_date_missing:
    "The uploaded Filled Ladder Plan does not contain a Data Dump Last_updated anchor date.",
  version_not_empty: "This version already contains uploaded or generated data and cannot be auto-deleted.",
};

function todayIso() {
  const now = new Date();
  const year = now.getFullYear();
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  const day = `${now.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function buildHeaders(user, accessInfo) {
  const headers = {};
  if (user?.email) headers["X-User-Email"] = user.email;
  if (user?.role) headers["X-User-Role"] = user.role || "";
  const activeCustTokens = Array.isArray(accessInfo?.activeCustTokens) ? accessInfo.activeCustTokens : [];
  const activeWhsTokens = Array.isArray(accessInfo?.activeWhsTokens) ? accessInfo.activeWhsTokens : [];
  if (activeCustTokens.length) headers["X-Active-Cust-Tokens"] = JSON.stringify(activeCustTokens);
  if (activeWhsTokens.length) headers["X-Active-Whs-Tokens"] = JSON.stringify(activeWhsTokens);
  return headers;
}

function resolveApiError(body, fallback) {
  const key = String(body?.error || body?.message || "").trim();
  if (!key) return fallback;
  if (key === "version_exists_same_walmart_week") {
    const existingId = Number(body?.existing_version?.id || 0);
    const weekStart = String(body?.week_start || "").trim();
    const weekEnd = String(body?.week_end || "").trim();
    const windowLabel = weekStart && weekEnd ? ` (${weekStart} to ${weekEnd})` : "";
    return existingId
      ? `A version already exists for this Walmart week${windowLabel}. Using existing version #${existingId}.`
      : `A version already exists for this Walmart week${windowLabel}.`;
  }
  if (key === "weekly_update_week_mismatch") {
    const uploadedWindow =
      body?.uploaded_week_start && body?.uploaded_week_end
        ? `${body.uploaded_week_start} to ${body.uploaded_week_end}`
        : "the uploaded week";
    const versionWindow =
      body?.version_week_start && body?.version_week_end
        ? `${body.version_week_start} to ${body.version_week_end}`
        : "the selected version week";
    return `The uploaded Filled Ladder Plan belongs to Walmart week ${uploadedWindow}, but the selected version belongs to ${versionWindow}.`;
  }
  return API_ERROR_MAP[key] || key;
}

function formatAllocationWeekOption(weekKey, rows) {
  const key = String(weekKey || "").trim();
  if (!key) return "";
  const match = Array.isArray(rows) ? rows.find((row) => row?.week_key === key && row?.week_label) : null;
  const label = String(match?.week_label || "").trim();
  return label ? `${label} - ${key}` : key;
}

export default function WalmartLadderPlan() {
  const user = getCurrentUser();
  const userEmail = user?.email || "";
  const userRole = user?.role || "";
  const accessVersion = useAccessOverrideVersion();
  const accessInfo = useMemo(() => {
    void accessVersion;
    return getUserAccessInfo();
  }, [accessVersion]);
  const headers = useMemo(
    () => buildHeaders({ email: userEmail, role: userRole }, accessInfo),
    [userEmail, userRole, accessInfo]
  );
  const activeCustTokens = Array.isArray(accessInfo?.activeCustTokens) ? accessInfo.activeCustTokens : [];
  const hasAllCustAccess = activeCustTokens.some(
    (token) => String(token || "").trim().toUpperCase() === ALL_ACCESS_TOKEN
  );
  const baseCustOptions = useMemo(() => {
    const raw = Array.isArray(accessInfo?.activeCustTokens) ? accessInfo.activeCustTokens : [];
    const cleaned = raw
      .map((token) => String(token || "").trim().toUpperCase())
      .filter((token) => token && token !== ALL_ACCESS_TOKEN && token !== NULL_ACCESS_TOKEN);
    return Array.from(new Set(cleaned)).sort((a, b) => a.localeCompare(b));
  }, [accessInfo?.activeCustTokens]);
  const [masterCustOptions, setMasterCustOptions] = useState([]);
  const [loadingCustOptions, setLoadingCustOptions] = useState(false);
  const [custOptionsError, setCustOptionsError] = useState("");
  const custOptions = useMemo(() => {
    if (hasAllCustAccess) return masterCustOptions;
    return baseCustOptions;
  }, [hasAllCustAccess, masterCustOptions, baseCustOptions]);

  const [versions, setVersions] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [outputs, setOutputs] = useState([]);
  const [allocationData, setAllocationData] = useState({
    rows: [],
    items: [],
    weeks: [],
    whse_options: [],
    current_week: null,
  });
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [busyAction, setBusyAction] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [generateElapsedSec, setGenerateElapsedSec] = useState(0);
  const dirtyVersionIdsRef = useRef(new Set());
  const [hasPendingFilledGeneration, setHasPendingFilledGeneration] = useState(false);

  const [createCustCode, setCreateCustCode] = useState(custOptions.length === 1 ? custOptions[0] : "");
  const [createVersionDate, setCreateVersionDate] = useState(todayIso());
  const [createNotes, setCreateNotes] = useState("");

  const [templateFile, setTemplateFile] = useState(null);
  const [dataDumpFile, setDataDumpFile] = useState(null);
  const [weeklyUpdateFile, setWeeklyUpdateFile] = useState(null);
  const [allocationWhseCode, setAllocationWhseCode] = useState("");
  const [allocationItemNbr, setAllocationItemNbr] = useState("");
  const [allocationWeekFrom, setAllocationWeekFrom] = useState("");
  const [allocationWeekTo, setAllocationWeekTo] = useState("");
  const [allocationMode, setAllocationMode] = useState("allocate_remaining");
  const [allocationValues, setAllocationValues] = useState({
    forecast_data_dump_qty: "",
    forecast_event_qty: "",
    supplier_flow_qty: "",
  });
  const [allocationMeasures, setAllocationMeasures] = useState({
    forecast_data_dump_qty: true,
    forecast_event_qty: true,
    supplier_flow_qty: true,
  });

  const selectedVersion = useMemo(
    () => versions.find((version) => Number(version.id) === Number(selectedVersionId)) || null,
    [versions, selectedVersionId]
  );
  const effectiveRowCount = Number(summary?.row_count ?? selectedVersion?.summary?.row_count ?? 0);
  const hasUploadedTemplate = Boolean(selectedVersion?.has_template);
  const hasUploadedDataDump = effectiveRowCount > 0;
  const isGenerating = busyAction === "generate-filled" || busyAction === "update-weekly";
  const generateProgressPct = Math.min(95, Math.round((generateElapsedSec / 120) * 100));
  const visibleAllocationRows = useMemo(() => {
    const rows = Array.isArray(allocationData?.rows) ? allocationData.rows : [];
    return rows.filter((row) => {
      if (allocationItemNbr && row.item_nbr !== allocationItemNbr) return false;
      if (allocationWeekFrom && row.week_key < allocationWeekFrom) return false;
      if (allocationWeekTo && row.week_key > allocationWeekTo) return false;
      return true;
    });
  }, [allocationData?.rows, allocationItemNbr, allocationWeekFrom, allocationWeekTo]);
  const allocationPendingFiles = useMemo(() => {
    const pending = [];
    if (templateFile) pending.push("Ladder Plan");
    if (dataDumpFile) pending.push("Data Dump");
    if (weeklyUpdateFile) pending.push("Update Supplier Flow / Event Forecast");
    return pending;
  }, [templateFile, dataDumpFile, weeklyUpdateFile]);
  const allocationVerificationPending =
    allocationPendingFiles.length > 0 ||
    ["upload-template", "upload-data-dump", "update-weekly"].includes(busyAction);

  const markVersionDirty = useCallback((versionId) => {
    const numericId = Number(versionId || 0);
    if (!numericId) return;
    dirtyVersionIdsRef.current.add(numericId);
    setHasPendingFilledGeneration(dirtyVersionIdsRef.current.size > 0);
  }, []);

  const clearVersionDirty = useCallback((versionId) => {
    const numericId = Number(versionId || 0);
    if (!numericId) return;
    dirtyVersionIdsRef.current.delete(numericId);
    setHasPendingFilledGeneration(dirtyVersionIdsRef.current.size > 0);
  }, []);

  const handleSelectVersion = useCallback(
    (nextVersionId) => {
      const nextId = Number(nextVersionId || 0);
      const currentId = Number(selectedVersionId || 0);
      if (!nextId || nextId === currentId) {
        setSelectedVersionId(nextVersionId);
        return;
      }
      if (currentId && dirtyVersionIdsRef.current.has(currentId)) {
        const shouldLeave = window.confirm(
          `Version #${currentId} has changes that are not yet saved as a Filled Ladder Plan. Leave this version anyway?`
        );
        if (!shouldLeave) return;
      }
      setSelectedVersionId(nextVersionId);
    },
    [selectedVersionId]
  );

  const mergeOutputRow = useCallback((nextOutput) => {
    if (!nextOutput?.id) return;
    setOutputs((prev) => {
      const existing = Array.isArray(prev) ? prev : [];
      const deduped = existing.filter((row) => Number(row?.id) !== Number(nextOutput.id));
      return [nextOutput, ...deduped];
    });
  }, []);

  useEffect(() => {
    if (!custOptions.length) {
      if (createCustCode) setCreateCustCode("");
      return;
    }
    if (createCustCode && custOptions.includes(createCustCode)) {
      return;
    }
    if (custOptions.length === 1) {
      setCreateCustCode(custOptions[0]);
      return;
    }
    setCreateCustCode("");
  }, [custOptions, createCustCode]);

  useEffect(() => {
    if (!hasAllCustAccess) {
      setMasterCustOptions([]);
      setLoadingCustOptions(false);
      setCustOptionsError("");
      return;
    }
    let cancelled = false;
    const CUST_KEYS = [
      "cust_code",
      "custCode",
      "customer_code",
      "customerCode",
      "code",
      "company_code",
      "companyCode",
    ];
    const loadMasterCustCodes = async () => {
      setLoadingCustOptions(true);
      setCustOptionsError("");
      try {
        const resp = await fetch(`${API_BASE}/customers`, { headers });
        const body = await resp.json().catch(() => ({}));
        if (!resp.ok || body?.ok !== true) {
          throw new Error(body?.error || "Unable to load customer list.");
        }
        const rows = Array.isArray(body?.rows) ? body.rows : [];
        const allCodes = rows.flatMap((row) =>
          CUST_KEYS.map((key) => row?.[key])
            .filter((value) => value !== undefined && value !== null)
            .map((value) => String(value).trim().toUpperCase())
            .filter(Boolean)
        );
        const deduped = Array.from(new Set(allCodes)).sort((a, b) => a.localeCompare(b));
        if (!cancelled) {
          setMasterCustOptions(deduped);
        }
      } catch (err) {
        if (!cancelled) {
          setMasterCustOptions([]);
          setCustOptionsError(err?.message || "Unable to load customer list.");
        }
      } finally {
        if (!cancelled) {
          setLoadingCustOptions(false);
        }
      }
    };
    loadMasterCustCodes();
    return () => {
      cancelled = true;
    };
  }, [hasAllCustAccess, headers]);

  const loadVersions = useCallback(async () => {
    setLoadingVersions(true);
    setError("");
    try {
      const resp = await fetch(`${API_BASE}/api/mrp/walmart-ladder/versions`, { headers });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body?.ok !== true) {
        throw new Error(resolveApiError(body, "Unable to load versions."));
      }
      const items = Array.isArray(body?.items) ? body.items : [];
      setVersions(items);
      setSelectedVersionId((prev) => {
        if (prev && items.some((item) => Number(item.id) === Number(prev))) return prev;
        return null;
      });
    } catch (err) {
      setError(err?.message || "Unable to load versions.");
    } finally {
      setLoadingVersions(false);
    }
  }, [headers]);

  const loadVersionDetails = useCallback(
    async (versionId) => {
      if (!versionId) return null;
      setLoadingDetails(true);
      setError("");
      try {
        const [summaryResp, outputsResp, allocationsResp] = await Promise.all([
          fetch(`${API_BASE}/api/mrp/walmart-ladder/versions/${versionId}/summary`, { headers }),
          fetch(`${API_BASE}/api/mrp/walmart-ladder/versions/${versionId}/outputs`, { headers }),
          fetch(`${API_BASE}/api/mrp/walmart-ladder/versions/${versionId}/allocations`, { headers }),
        ]);
        const summaryBody = await summaryResp.json().catch(() => ({}));
        const outputsBody = await outputsResp.json().catch(() => ({}));
        const allocationsBody = await allocationsResp.json().catch(() => ({}));
        if (!summaryResp.ok || summaryBody?.ok !== true) {
          throw new Error(resolveApiError(summaryBody, "Unable to load summary."));
        }
        if (!outputsResp.ok || outputsBody?.ok !== true) {
          throw new Error(resolveApiError(outputsBody, "Unable to load outputs."));
        }
        if (!allocationsResp.ok || allocationsBody?.ok !== true) {
          throw new Error(resolveApiError(allocationsBody, "Unable to load warehouse allocations."));
        }
        const nextSummary = summaryBody?.summary || null;
        const nextOutputs = Array.isArray(outputsBody?.items) ? outputsBody.items : [];
        const nextAllocations = {
          rows: Array.isArray(allocationsBody?.rows) ? allocationsBody.rows : [],
          items: Array.isArray(allocationsBody?.items) ? allocationsBody.items : [],
          weeks: Array.isArray(allocationsBody?.weeks) ? allocationsBody.weeks : [],
          whse_options: Array.isArray(allocationsBody?.whse_options) ? allocationsBody.whse_options : [],
          current_week: allocationsBody?.current_week || null,
        };
        setSummary(nextSummary);
        setOutputs(nextOutputs);
        setAllocationData(nextAllocations);
        return {
          summary: nextSummary,
          outputs: nextOutputs,
          allocations: nextAllocations,
        };
      } catch (err) {
        setError(err?.message || "Unable to load version details.");
        return null;
      } finally {
        setLoadingDetails(false);
      }
    },
    [headers]
  );

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  useEffect(() => {
    if (selectedVersionId) {
      loadVersionDetails(selectedVersionId);
    } else {
      setSummary(null);
      setOutputs([]);
      setAllocationData({ rows: [], items: [], weeks: [], whse_options: [], current_week: null });
    }
  }, [selectedVersionId, loadVersionDetails]);

  useEffect(() => {
    setWeeklyUpdateFile(null);
  }, [selectedVersionId]);

  useEffect(() => {
    if (!isGenerating) {
      setGenerateElapsedSec(0);
      return undefined;
    }
    setGenerateElapsedSec(0);
    const timer = window.setInterval(() => {
      setGenerateElapsedSec((prev) => prev + 1);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [isGenerating]);

  useBeforeUnload(
    useCallback(
      (event) => {
        if (!hasPendingFilledGeneration) return;
        event.preventDefault();
        event.returnValue = "";
      },
      [hasPendingFilledGeneration]
    )
  );

  useEffect(() => {
    if (!hasPendingFilledGeneration) return undefined;
    const handleDocumentClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest("a[href]");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) return;
      const shouldLeave = window.confirm(
        "This version has changes that are not yet saved as a Filled Ladder Plan. Leave this page anyway?"
      );
      if (!shouldLeave) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    document.addEventListener("click", handleDocumentClick, true);
    return () => document.removeEventListener("click", handleDocumentClick, true);
  }, [hasPendingFilledGeneration]);

  useEffect(() => {
    const whseOptions = Array.isArray(allocationData?.whse_options) ? allocationData.whse_options : [];
    if (!whseOptions.length) {
      if (allocationWhseCode) setAllocationWhseCode("");
      return;
    }
    if (allocationWhseCode && whseOptions.includes(allocationWhseCode)) return;
    setAllocationWhseCode(whseOptions[0]);
  }, [allocationData?.whse_options, allocationWhseCode]);

  useEffect(() => {
    const weeks = Array.isArray(allocationData?.weeks) ? allocationData.weeks : [];
    if (!weeks.length) {
      if (allocationWeekFrom) setAllocationWeekFrom("");
      if (allocationWeekTo) setAllocationWeekTo("");
      return;
    }
    if (!allocationWeekFrom || !weeks.includes(allocationWeekFrom)) {
      setAllocationWeekFrom(weeks[0]);
    }
    if (!allocationWeekTo || !weeks.includes(allocationWeekTo)) {
      setAllocationWeekTo(weeks[weeks.length - 1]);
    }
  }, [allocationData?.weeks, allocationWeekFrom, allocationWeekTo]);

  const deleteEmptyVersion = useCallback(
    async (versionId) => {
      if (!versionId) return false;
      const resp = await fetch(`${API_BASE}/api/mrp/walmart-ladder/versions/${versionId}`, {
        method: "DELETE",
        headers,
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body?.ok !== true) {
        return false;
      }
      return true;
    },
    [headers]
  );

  const handleCreateVersion = async () => {
    if (busyAction) return;
    setBusyAction("create");
    setError("");
    setNotice("");
    try {
      const payload = {
        cust_code: createCustCode || undefined,
        version_date: createVersionDate || undefined,
        notes: createNotes || undefined,
      };
      const resp = await fetch(`${API_BASE}/api/mrp/walmart-ladder/versions`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body?.ok !== true) {
        if (resp.status === 409 && body?.error === "version_exists_same_walmart_week" && body?.existing_version?.id) {
          const existingVersionId = Number(body.existing_version.id);
          setSelectedVersionId(existingVersionId);
          setError(resolveApiError(body, "A version already exists for this Walmart week."));
          await loadVersions();
          await loadVersionDetails(existingVersionId);
          return;
        }
        throw new Error(resolveApiError(body, "Unable to create version."));
      }
      setNotice("Version created.");
      await loadVersions();
      if (body?.version?.id) {
        setSelectedVersionId(body.version.id);
      }
    } catch (err) {
      setError(err?.message || "Unable to create version.");
    } finally {
      setBusyAction("");
    }
  };

  const handleTemplateUpload = async () => {
    if (!selectedVersionId || !templateFile || busyAction) return;
    setBusyAction("upload-template");
    setError("");
    setNotice("");
    try {
      const form = new FormData();
      form.append("file", templateFile);
      const resp = await fetch(`${API_BASE}/api/mrp/walmart-ladder/versions/${selectedVersionId}/upload-template`, {
        method: "POST",
        headers,
        body: form,
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body?.ok !== true) {
        throw new Error(resolveApiError(body, "Unable to upload template."));
      }
      const supplierFlowItems = Number(body?.template_weekly_ingest?.supplier_flow?.items || 0);
      const eventForecastItems = Number(body?.template_weekly_ingest?.event_forecast?.items || 0);
      setNotice(
        `Ladder Plan uploaded. Weekly sheets reloaded from template (${supplierFlowItems} supplier-flow items, ${eventForecastItems} event-forecast items).`
      );
      markVersionDirty(selectedVersionId);
      setTemplateFile(null);
      await loadVersions();
      await loadVersionDetails(selectedVersionId);
    } catch (err) {
      setError(err?.message || "Unable to upload template.");
    } finally {
      setBusyAction("");
    }
  };

  const uploadDataDumpToVersion = async (targetVersionId, allowConflictPrompt = true) => {
    if (!targetVersionId || !dataDumpFile) return;
    setBusyAction("upload-data-dump");
    setError("");
    setNotice("");
    try {
      const form = new FormData();
      form.append("file", dataDumpFile);
      const resp = await fetch(`${API_BASE}/api/mrp/walmart-ladder/versions/${targetVersionId}/upload-data-dump`, {
        method: "POST",
        headers,
        body: form,
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body?.ok !== true) {
        if (
          allowConflictPrompt &&
          resp.status === 409 &&
          body?.error === "version_exists_same_walmart_week" &&
          Number(body?.existing_version?.id || 0) > 0
        ) {
          const placeholderVersionId = Number(targetVersionId);
          const existingVersionId = Number(body.existing_version.id);
          const weekStart = String(body?.week_start || "").trim();
          const weekEnd = String(body?.week_end || "").trim();
          const promptMessage = `Walmart Week existed${weekStart && weekEnd ? ` (${weekStart} to ${weekEnd})` : ""}.\n\nUpdate existing version ID ${existingVersionId} instead?`;
          const shouldContinue = window.confirm(promptMessage);
          if (!shouldContinue) {
            const deleted = await deleteEmptyVersion(placeholderVersionId);
            await loadVersions();
            setSelectedVersionId(existingVersionId);
            await loadVersionDetails(existingVersionId);
            setError("");
            setNotice(
              deleted
                ? `Data Dump upload aborted. Empty version #${placeholderVersionId} was deleted.`
                : "Data Dump upload aborted."
            );
            return;
          }
          const deleted = await deleteEmptyVersion(placeholderVersionId);
          await loadVersions();
          setSelectedVersionId(existingVersionId);
          await loadVersionDetails(existingVersionId);
          await uploadDataDumpToVersion(existingVersionId, false);
          if (deleted) {
            setNotice((prev) =>
              prev
                ? `${prev} Empty version #${placeholderVersionId} was deleted.`
                : `Empty version #${placeholderVersionId} was deleted.`
            );
          }
          return;
        }
        throw new Error(resolveApiError(body, "Unable to upload data dump."));
      }
      const supplierFlowItems = Number(body?.template_weekly_ingest?.supplier_flow?.items || 0);
      const eventForecastItems = Number(body?.template_weekly_ingest?.event_forecast?.items || 0);
      const weeklyReloadNotice =
        body?.template_weekly_ingest
          ? ` Weekly sheets re-aligned from template (${supplierFlowItems} supplier-flow items, ${eventForecastItems} event-forecast items).`
          : "";
      const switchedLabel =
        Number(targetVersionId) !== Number(selectedVersionId)
          ? ` Data Dump was uploaded to existing version #${targetVersionId}.`
          : "";
      setNotice(`Data dump uploaded and parsed.${weeklyReloadNotice}${switchedLabel}`);
      markVersionDirty(targetVersionId);
      setDataDumpFile(null);
      setSummary(body?.summary || null);
      await loadVersions();
      setSelectedVersionId(targetVersionId);
      await loadVersionDetails(targetVersionId);
    } catch (err) {
      setError(err?.message || "Unable to upload data dump.");
    } finally {
      setBusyAction("");
    }
  };

  const handleDataDumpUpload = async () => {
    if (!selectedVersionId || !dataDumpFile || busyAction) return;
    await uploadDataDumpToVersion(selectedVersionId, true);
  };

  const handleGenerateFilled = async () => {
    if (!selectedVersionId || busyAction) return;
    if (!hasUploadedTemplate) {
      setError("Please upload a template file first.");
      setNotice("");
      return;
    }
    if (!hasUploadedDataDump) {
      setError("Please upload and parse a data dump file first.");
      setNotice("");
      return;
    }
    setBusyAction("generate-filled");
    setError("");
    setNotice("");
    const previousFilledOutputIds = new Set(
      (Array.isArray(outputs) ? outputs : [])
        .filter((row) => String(row?.output_type || "").trim() === "filled_xlsm")
        .map((row) => Number(row?.id || 0))
        .filter((id) => id > 0)
    );
    try {
      const resp = await fetch(
        `${API_BASE}/api/mrp/walmart-ladder/versions/${selectedVersionId}/generate-filled-xlsm`,
        { method: "POST", headers }
      );
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body?.ok !== true) {
        throw new Error(resolveApiError(body, "Unable to generate filled ladder plan."));
      }
      clearVersionDirty(selectedVersionId);
      mergeOutputRow(body?.output);
      setNotice("Filled Ladder Plan generated.");
      await loadVersions();
      await loadVersionDetails(selectedVersionId);
    } catch (err) {
      const rawMessage = String(err?.message || "").trim();
      const networkLikeFailure =
        !rawMessage ||
        /failed to fetch/i.test(rawMessage) ||
        /networkerror/i.test(rawMessage) ||
        /load failed/i.test(rawMessage);
      if (networkLikeFailure) {
        await loadVersions();
        const refreshed = await loadVersionDetails(selectedVersionId);
        const refreshedFilled = (Array.isArray(refreshed?.outputs) ? refreshed.outputs : []).filter(
          (row) => String(row?.output_type || "").trim() === "filled_xlsm"
        );
        const recoveredOutput = refreshedFilled.find((row) => !previousFilledOutputIds.has(Number(row?.id || 0)));
        if (recoveredOutput) {
          clearVersionDirty(selectedVersionId);
          mergeOutputRow(recoveredOutput);
          setError("");
          setNotice(
            "The request connection was interrupted, but the Filled Ladder Plan was generated successfully."
          );
          return;
        }
      }
      setError(rawMessage || "Unable to generate filled ladder plan.");
    } finally {
      setBusyAction("");
    }
  };

  const handleApplyAllocation = async () => {
    if (!selectedVersionId || busyAction) return;
    const rows = visibleAllocationRows;
    const itemNbrs = Array.from(new Set(rows.map((row) => row.item_nbr).filter(Boolean)));
    const weekKeys = Array.from(new Set(rows.map((row) => row.week_key).filter(Boolean)));
    const measureKeys = Object.entries(allocationMeasures)
      .filter(([, enabled]) => enabled)
      .map(([key]) => key);
    if (!allocationWhseCode) {
      setError("Please select a warehouse.");
      setNotice("");
      return;
    }
    if (!itemNbrs.length || !weekKeys.length) {
      setError("No ladder MRP series rows are selected for allocation.");
      setNotice("");
      return;
    }
    setBusyAction("allocation-save");
    setError("");
    setNotice("");
    try {
      const payload = {
        whse_code: allocationWhseCode,
        item_nbrs: itemNbrs,
        week_keys: weekKeys,
        mode: allocationMode,
        measure_keys: measureKeys,
        forecast_data_dump_qty:
          allocationValues.forecast_data_dump_qty === "" ? null : Number(allocationValues.forecast_data_dump_qty),
        forecast_event_qty:
          allocationValues.forecast_event_qty === "" ? null : Number(allocationValues.forecast_event_qty),
        supplier_flow_qty:
          allocationValues.supplier_flow_qty === "" ? null : Number(allocationValues.supplier_flow_qty),
      };
      const resp = await fetch(`${API_BASE}/api/mrp/walmart-ladder/versions/${selectedVersionId}/allocations`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body?.ok !== true) {
        throw new Error(resolveApiError(body, "Unable to save warehouse allocation."));
      }
      setAllocationData({
        rows: Array.isArray(body?.rows) ? body.rows : [],
        items: Array.isArray(body?.items) ? body.items : [],
        weeks: Array.isArray(body?.weeks) ? body.weeks : [],
        whse_options: Array.isArray(body?.whse_options) ? body.whse_options : [],
        current_week: body?.current_week || null,
      });
      setNotice(`Warehouse allocation saved for ${body?.touched_rows || 0} row(s).`);
      await loadVersionDetails(selectedVersionId);
    } catch (err) {
      setError(err?.message || "Unable to save warehouse allocation.");
    } finally {
      setBusyAction("");
    }
  };

  const handleGenerateCompiledStub = async () => {
    if (!selectedVersionId || busyAction) return;
    setBusyAction("generate-compiled");
    setError("");
    setNotice("");
    try {
      const resp = await fetch(
        `${API_BASE}/api/mrp/walmart-ladder/versions/${selectedVersionId}/generate-compiled-xlsm`,
        { method: "POST", headers }
      );
      const body = await resp.json().catch(() => ({}));
      if (resp.status === 409) {
        setNotice(body?.message || "Compiled XLSM generation is not available.");
        return;
      }
      if (!resp.ok || body?.ok !== true) {
        throw new Error(resolveApiError(body, "Unable to trigger compiled XLSM generation."));
      }
      setNotice("Compiled XLSM generated.");
    } catch (err) {
      setError(err?.message || "Unable to trigger compiled XLSM generation.");
    } finally {
      setBusyAction("");
    }
  };

  const handleWeeklyUpdate = async () => {
    if (!selectedVersionId || !weeklyUpdateFile || busyAction) return;
    if (!hasUploadedTemplate) {
      setError("Please upload a template file first.");
      setNotice("");
      return;
    }
    if (!hasUploadedDataDump) {
      setError("Please upload and parse a data dump file first.");
      setNotice("");
      return;
    }
    setBusyAction("update-weekly");
    setError("");
    setNotice("");
    try {
      const form = new FormData();
      form.append("file", weeklyUpdateFile);
      const resp = await fetch(
        `${API_BASE}/api/mrp/walmart-ladder/versions/${selectedVersionId}/update-weekly-sheets`,
        {
          method: "POST",
          headers,
          body: form,
        }
      );
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body?.ok !== true) {
        if (body?.error === "weekly_update_week_mismatch") {
          setWeeklyUpdateFile(null);
        }
        throw new Error(resolveApiError(body, "Unable to update Supplier Flow / Event Forecast."));
      }
      const supplierFlowItems = Number(body?.supplier_flow_update?.items || 0);
      const eventForecastItems = Number(body?.event_forecast_update?.items || 0);
      clearVersionDirty(selectedVersionId);
      mergeOutputRow(body?.output);
      setNotice(
        `Supplier Flow / Event Forecast updated (${supplierFlowItems} supplier-flow items, ${eventForecastItems} event-forecast items). Filled Ladder Plan regenerated.`
      );
      setWeeklyUpdateFile(null);
      await loadVersions();
      await loadVersionDetails(selectedVersionId);
    } catch (err) {
      setError(err?.message || "Unable to update Supplier Flow / Event Forecast.");
    } finally {
      setBusyAction("");
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-4">
      <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Walmart Ladder Plan</h2>
            <p className="mt-1 text-sm text-slate-500">
              Create versions, upload template and data dump, generate filled XLSM, and export MRP series.
            </p>
          </div>
          <UserAccessSummary />
        </div>
      </div>

      {(error || notice) && (
        <div
          className={`rounded-lg border px-4 py-3 text-sm ${
            error ? "border-red-200 bg-red-50 text-red-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"
          }`}
        >
          {error || notice}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700">Create Version</h3>
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="text-sm text-slate-600">
              Cust Code
              <select
                value={createCustCode}
                onChange={(e) => setCreateCustCode(e.target.value.toUpperCase())}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                disabled={loadingCustOptions || !custOptions.length}
              >
                <option value="">
                  {loadingCustOptions
                    ? "Loading cust codes..."
                    : custOptions.length
                      ? "Select cust code"
                      : "No accessible cust code"}
                </option>
                {custOptions.map((cust) => (
                  <option key={cust} value={cust}>
                    {cust}
                  </option>
                ))}
              </select>
              {!!custOptionsError && <div className="mt-1 text-xs text-red-600">{custOptionsError}</div>}
            </label>
            <label className="text-sm text-slate-600">
              Version Date
              <input
                type="date"
                value={createVersionDate}
                onChange={(e) => setCreateVersionDate(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="text-sm text-slate-600 md:col-span-2">
              Notes
              <input
                value={createNotes}
                onChange={(e) => setCreateNotes(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                placeholder="Optional"
              />
            </label>
          </div>
          <button
            type="button"
            onClick={handleCreateVersion}
            disabled={busyAction === "create" || !createCustCode}
            className="mt-3 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busyAction === "create" ? "Creating..." : "Create Version"}
          </button>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700">Versions</h3>
            <button
              type="button"
              onClick={loadVersions}
              disabled={loadingVersions}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              {loadingVersions ? "Refreshing..." : "Refresh"}
            </button>
          </div>
          <div className="mt-3 max-h-64 overflow-auto rounded-md border border-slate-200">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-3 py-2">ID</th>
                  <th className="px-3 py-2">Cust</th>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Storage</th>
                </tr>
              </thead>
              <tbody>
                {versions.map((version) => {
                  const selected = Number(version.id) === Number(selectedVersionId);
                  return (
                    <tr
                      key={version.id}
                      onClick={() => handleSelectVersion(version.id)}
                      className={`cursor-pointer border-t border-slate-100 ${selected ? "bg-blue-50" : "hover:bg-slate-50"}`}
                    >
                      <td className="px-3 py-2">{version.id}</td>
                      <td className="px-3 py-2">{version.cust_code || "-"}</td>
                      <td className="px-3 py-2">{version.version_date || "-"}</td>
                      <td className="px-3 py-2">{version.status || "-"}</td>
                      <td className="px-3 py-2">{version.storage_mode || "-"}</td>
                    </tr>
                  );
                })}
                {!versions.length && (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-center text-slate-500">
                      No versions available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm lg:col-span-2">
          <h3 className="text-sm font-semibold text-slate-700">Uploads</h3>
          <div className="mt-2 text-xs text-slate-500">
            Selected Version: {selectedVersion ? `#${selectedVersion.id}` : "None"}.
            {!selectedVersion ? " Select a version from the table or create a new version before upload." : ""}
          </div>
          <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Ladder Plan (.xlsm)</div>
              <input
                type="file"
                accept=".xlsm"
                onChange={(e) => setTemplateFile(e.target.files?.[0] || null)}
                className="mt-2 block w-full rounded-md border border-slate-300 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-slate-700 hover:file:bg-slate-200"
              />
              <button
                type="button"
                onClick={handleTemplateUpload}
                disabled={!selectedVersionId || !templateFile || busyAction === "upload-template"}
                className="mt-2 rounded-md bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {busyAction === "upload-template" ? "Uploading..." : "Upload Ladder Plan"}
              </button>
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Data Dump (.csv)</div>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setDataDumpFile(e.target.files?.[0] || null)}
                className="mt-2 block w-full rounded-md border border-slate-300 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-slate-700 hover:file:bg-slate-200"
              />
              <button
                type="button"
                onClick={handleDataDumpUpload}
                disabled={!selectedVersionId || !dataDumpFile || busyAction === "upload-data-dump"}
                className="mt-2 rounded-md bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {busyAction === "upload-data-dump" ? "Uploading..." : "Upload Data Dump"}
              </button>
            </div>
          </div>

          <div className="mt-5 border-t border-slate-200 pt-4">
            <h4 className="text-sm font-semibold text-slate-700">Update Supplier Flow / Event Forecast</h4>
            <div className="mt-1 text-xs text-slate-500">
              Upload an edited Filled Ladder Plan. Only Supplier Flow weekly values and Event Forecast weekly values
              will be ingested. Supplier OH remains system-derived.
            </div>
            <div className="mt-3 max-w-xl">
              <input
                type="file"
                accept=".xlsm"
                onChange={(e) => setWeeklyUpdateFile(e.target.files?.[0] || null)}
                className="block w-full rounded-md border border-slate-300 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-slate-700 hover:file:bg-slate-200"
              />
              <button
                type="button"
                onClick={handleWeeklyUpdate}
                disabled={
                  !selectedVersionId ||
                  !weeklyUpdateFile ||
                  !hasUploadedTemplate ||
                  !hasUploadedDataDump ||
                  busyAction === "update-weekly"
                }
                className="mt-2 rounded-md bg-slate-800 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {busyAction === "update-weekly" ? "Updating..." : "Update Supplier Flow / Event Forecast"}
              </button>
            </div>
          </div>

          <div className="mt-5 border-t border-slate-200 pt-4">
            <h4 className="text-sm font-semibold text-slate-700">Generate Outputs</h4>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleGenerateFilled}
                disabled={
                  !selectedVersionId ||
                  !hasUploadedTemplate ||
                  !hasUploadedDataDump ||
                  busyAction === "generate-filled"
                }
                className="rounded-md bg-blue-600 px-4 py-2 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {busyAction === "generate-filled" ? "Generating..." : "Generate Filled Ladder Plan"}
              </button>
              <button
                type="button"
                onClick={handleGenerateCompiledStub}
                disabled={!selectedVersionId || busyAction === "generate-compiled"}
                className="rounded-md border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
              >
                Generate Compiled XLSM (Stub)
              </button>
            </div>
            <div className="mt-2 text-xs text-slate-500">
              Ladder MRP series is rebuilt automatically after template/data/update actions. Compiled XLSM is not
              available yet; the stub endpoint returns HTTP 409 by design.
            </div>
            {isGenerating && (
              <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                <div className="flex items-center justify-between text-xs font-medium text-amber-800">
                  <span>Processing file. This may take about 1-2 minutes.</span>
                  <span>{generateProgressPct}%</span>
                </div>
                <div className="mt-2 h-2 w-full rounded-full bg-amber-100">
                  <div
                    className="h-2 rounded-full bg-amber-500 transition-all duration-700"
                    style={{ width: `${generateProgressPct}%` }}
                  />
                </div>
                <div className="mt-1 text-[11px] text-amber-700">Elapsed: {generateElapsedSec}s</div>
              </div>
            )}
            {(error || notice) && (
              <div
                className={`mt-3 rounded-md border px-3 py-2 text-xs ${
                  error ? "border-red-200 bg-red-50 text-red-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"
                }`}
              >
                {error || notice}
              </div>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700">Parse Summary</h3>
          <div className="mt-2 space-y-1 text-sm text-slate-600">
            <div>Version: {selectedVersion ? `#${selectedVersion.id}` : "-"}</div>
            <div>Rows: {selectedVersion ? effectiveRowCount : "-"}</div>
            <div>Distinct Item Nbr: {summary?.distinct_item_nbr ?? "-"}</div>
            <div>Distinct Vendor Nbr 9: {summary?.distinct_vendor_nbr_9 ?? "-"}</div>
            <div>Last Updated Min: {summary?.last_updated_min || "-"}</div>
            <div>Last Updated Max: {summary?.last_updated_max || "-"}</div>
            {loadingDetails && <div className="pt-1 text-xs text-slate-500">Loading details...</div>}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">Warehouse Allocation</h3>
            <div className="mt-1 text-xs text-slate-500">
              Allocate Ladder MRP measures to warehouse by version. MRP AIO reads these allocations by warehouse.
            </div>
            {allocationData?.current_week?.label ? (
              <div className="mt-1 text-xs font-medium text-slate-600">
                Current Week: {allocationData.current_week.label} ({allocationData.current_week.week_start} to{" "}
                {allocationData.current_week.week_end})
              </div>
            ) : null}
          </div>
          {!allocationVerificationPending && (
            <div className="text-xs text-slate-500">
              Rows: {visibleAllocationRows.length} / {Array.isArray(allocationData?.rows) ? allocationData.rows.length : 0}
            </div>
          )}
        </div>
        {allocationVerificationPending ? (
          <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Complete the pending file action before warehouse allocation is available.
            {allocationPendingFiles.length ? ` Pending: ${allocationPendingFiles.join(", ")}.` : ""}
          </div>
        ) : (
          <>
            <div className="mt-4 grid grid-cols-1 gap-3 xl:grid-cols-6">
              <label className="text-sm text-slate-600">
                Warehouse
                <select
                  value={allocationWhseCode}
                  onChange={(e) => setAllocationWhseCode(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  disabled={!allocationData?.whse_options?.length}
                >
                  <option value="">{allocationData?.whse_options?.length ? "Select warehouse" : "No warehouse options"}</option>
                  {(allocationData?.whse_options || []).map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
              </label>

              <label className="text-sm text-slate-600">
                Item
                <select
                  value={allocationItemNbr}
                  onChange={(e) => setAllocationItemNbr(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value="">All items</option>
                  {(allocationData?.items || []).map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </label>

              <label className="text-sm text-slate-600">
                Week From
                <select
                  value={allocationWeekFrom}
                  onChange={(e) => setAllocationWeekFrom(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value="">All</option>
                  {(allocationData?.weeks || []).map((week) => (
                    <option key={week} value={week}>
                      {formatAllocationWeekOption(week, allocationData?.rows)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="text-sm text-slate-600">
                Week To
                <select
                  value={allocationWeekTo}
                  onChange={(e) => setAllocationWeekTo(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value="">All</option>
                  {(allocationData?.weeks || []).map((week) => (
                    <option key={week} value={week}>
                      {formatAllocationWeekOption(week, allocationData?.rows)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="text-sm text-slate-600">
                Mode
                <select
                  value={allocationMode}
                  onChange={(e) => setAllocationMode(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value="allocate_remaining">Allocate Remaining</option>
                  <option value="set_absolute">Set Qty</option>
                  <option value="clear_selected">Clear Qty</option>
                </select>
              </label>

              <div className="text-sm text-slate-600">
                Measures
                <div className="mt-1 flex flex-wrap gap-2 rounded-md border border-slate-300 px-3 py-2 text-xs">
                  {[
                    ["forecast_data_dump_qty", "Forecast - Data Dump"],
                    ["forecast_event_qty", "Forecast - Event Code"],
                    ["supplier_flow_qty", "Supplier Flow - WM Delivery"],
                  ].map(([key, label]) => (
                    <label key={key} className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={Boolean(allocationMeasures[key])}
                        onChange={(e) =>
                          setAllocationMeasures((prev) => ({
                            ...prev,
                            [key]: e.target.checked,
                          }))
                        }
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {allocationMode === "set_absolute" && (
              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                <label className="text-sm text-slate-600">
                  Forecast - Data Dump Qty
                  <input
                    type="number"
                    step="0.01"
                    value={allocationValues.forecast_data_dump_qty}
                    onChange={(e) =>
                      setAllocationValues((prev) => ({ ...prev, forecast_data_dump_qty: e.target.value }))
                    }
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>
                <label className="text-sm text-slate-600">
                  Forecast - Event Code Qty
                  <input
                    type="number"
                    step="0.01"
                    value={allocationValues.forecast_event_qty}
                    onChange={(e) =>
                      setAllocationValues((prev) => ({ ...prev, forecast_event_qty: e.target.value }))
                    }
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>
                <label className="text-sm text-slate-600">
                  Supplier Flow - WM Delivery Qty
                  <input
                    type="number"
                    step="0.01"
                    value={allocationValues.supplier_flow_qty}
                    onChange={(e) => setAllocationValues((prev) => ({ ...prev, supplier_flow_qty: e.target.value }))}
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>
              </div>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleApplyAllocation}
                disabled={!selectedVersionId || !visibleAllocationRows.length || busyAction === "allocation-save"}
                className="rounded-md bg-slate-800 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-900 disabled:opacity-60"
              >
                {busyAction === "allocation-save" ? "Saving..." : "Apply Allocation"}
              </button>
              <div className="text-xs text-slate-500">
                {allocationMode === "allocate_remaining" &&
                  "`Allocate Remaining` assigns the current unallocated qty to the selected warehouse for the selected rows."}
                {allocationMode === "set_absolute" &&
                  "`Set Qty` overwrites the selected warehouse allocation to the entered qty for the selected rows."}
                {allocationMode === "clear_selected" &&
                  "`Clear Qty` sets the selected warehouse allocation to 0 for the selected rows."}
              </div>
            </div>

            <div className="mt-4 h-[30rem] overflow-auto rounded-md border border-slate-200">
              <table className="min-w-[1400px] w-full text-left text-xs">
                <thead className="sticky top-0 bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2">Item</th>
                    <th className="px-3 py-2">Week Label</th>
                    <th className="px-3 py-2">Week</th>
                    <th className="px-3 py-2">Ttl Data Dump</th>
                    <th className="px-3 py-2">Ttl Event FC</th>
                    <th className="px-3 py-2">Ttl Supplier Flow</th>
                    <th className="px-3 py-2">Bal Data Dump</th>
                    <th className="px-3 py-2">Bal Event FC</th>
                    <th className="px-3 py-2">Bal Supplier Flow</th>
                    <th className="px-3 py-2">Allocations</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleAllocationRows.map((row) => (
                    <tr key={`${row.item_nbr}-${row.week_key}`} className="border-t border-slate-100">
                      <td className="px-3 py-2">{row.item_nbr}</td>
                      <td className="px-3 py-2">{row.week_label || "-"}</td>
                      <td className="px-3 py-2">{row.week_key}</td>
                      <td className="px-3 py-2">{row.forecast_data_dump_qty ?? 0}</td>
                      <td className="px-3 py-2">{row.forecast_event_qty ?? 0}</td>
                      <td className="px-3 py-2">{row.supplier_flow_qty ?? 0}</td>
                      <td className="px-3 py-2">{row.remaining_totals?.forecast_data_dump_qty ?? 0}</td>
                      <td className="px-3 py-2">{row.remaining_totals?.forecast_event_qty ?? 0}</td>
                      <td className="px-3 py-2">{row.remaining_totals?.supplier_flow_qty ?? 0}</td>
                      <td className="px-3 py-2 text-slate-600">
                        {Array.isArray(row.allocations) && row.allocations.length
                          ? row.allocations
                              .map(
                                (alloc) =>
                                  `${alloc.whse_code}: FD ${alloc.forecast_data_dump_qty ?? 0}, FE ${
                                    alloc.forecast_event_qty ?? 0
                                  }, SF ${alloc.supplier_flow_qty ?? 0}`
                              )
                              .join(" | ")
                          : "-"}
                      </td>
                    </tr>
                  ))}
                  {!visibleAllocationRows.length && (
                    <tr>
                      <td colSpan={10} className="px-3 py-6 text-center text-slate-500">
                        No allocation rows available for the current filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-700">Output History</h3>
        <div className="mt-3 overflow-auto rounded-md border border-slate-200">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Filename</th>
                <th className="px-3 py-2">Created At</th>
                <th className="px-3 py-2">Download</th>
              </tr>
            </thead>
            <tbody>
              {outputs.map((row) => (
                <tr key={row.id} className="border-t border-slate-100">
                  <td className="px-3 py-2">{row.output_type || "-"}</td>
                  <td className="px-3 py-2">{row.filename || "-"}</td>
                  <td className="px-3 py-2">{row.created_at || "-"}</td>
                  <td className="px-3 py-2">
                    {row.download_url ? (
                      <a
                        href={row.download_url}
                        target="_blank"
                        rel="noreferrer"
                        className="font-semibold text-blue-600 hover:text-blue-800"
                      >
                        Download
                      </a>
                    ) : (
                      <span className="text-slate-400">Unavailable</span>
                    )}
                  </td>
                </tr>
              ))}
              {!outputs.length && (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-slate-500">
                    No outputs generated yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
